/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */

document.addEventListener("DOMContentLoaded", () => {
    const makeityourSelectButton = document.getElementById("makeityour-select");

    const goldImagesWrapper = document.querySelectorAll("[data-gold]");
    const silverImagesWrapper = document.querySelectorAll("[data-silver]");

    const sliderThumbnailImage = document.querySelector(".thumbnails [data-engrave]");
    const sliderPreviewImage = document.querySelector(".main-image [data-engrave]");
    // console.log("goldImagesWrappergoldImagesWrapper", goldImagesWrapper)

    // new code
    const previewImages = document.querySelectorAll(".preview__image-container");
    const engravingTexts = document.querySelectorAll(".engraving__text");
    const savedEngravedText = document.getElementById("saved-engraved-text");
    const thumbnailMediaImage = document.querySelectorAll(".thumbnail--media-image");
    const makeityourContainer = document.querySelector(".makeityour__container");
    const eternalizeCheckbox = document.getElementById("eternalize");

    const engravedTextMobile = document.querySelectorAll(".engraving__text-mobile");
    const engravingMobilePreview = document.getElementById("engraving-mobile-preview");

    const overlay = document.getElementById("engravingOverlay");
    const overlayInput = document.getElementById("overlayEngravingInput");

    const defaultColor = document.querySelector("variant-picker .option-selector custom-select .custom-select__btn.input span");
    let selectedColor = defaultColor?.textContent?.toLowerCase() || "";

    const showCorrectVarinatCustomizeImage = (color) => {
        if(color === "gold"){
            const imageUrl = sliderPreviewImage?.dataset?.goldImage;
            if(imageUrl?.length > 1){
                const thumbnailImgTag = sliderThumbnailImage.querySelector("img");
                const previewImgTag = sliderPreviewImage.querySelector("img");
                thumbnailImgTag.src = imageUrl;
                previewImgTag.src = imageUrl;
                thumbnailImgTag.removeAttribute('srcset');
                previewImgTag.removeAttribute('srcset');
            }

            goldImagesWrapper?.forEach((wrap) => {
                wrap.style.display = "block";
            })
            silverImagesWrapper?.forEach((wrap) => {
                wrap.style.display = "none";
            })
            sliderThumbnailImage.style.display = "block";
            sliderPreviewImage.style.display = "block";
        } else if(color === "silver" || color === "white gold"){
            const imageUrl = sliderPreviewImage?.dataset?.silverImage;
            if(imageUrl?.length > 1){
                const thumbnailImgTag = sliderThumbnailImage.querySelector("img");
                const previewImgTag = sliderPreviewImage.querySelector("img");
                thumbnailImgTag.src = imageUrl;
                previewImgTag.src = imageUrl;
                thumbnailImgTag.removeAttribute('srcset');
                previewImgTag.removeAttribute('srcset');
            }

            goldImagesWrapper?.forEach((wrap) => {
                wrap.style.display = "none";
            })
            silverImagesWrapper?.forEach((wrap) => {
                wrap.style.display = "block";
            })
            sliderThumbnailImage.style.display = "block";
            sliderPreviewImage.style.display = "block";
        } else {
            goldImagesWrapper?.forEach((wrap) => {
                wrap.style.display = "none";
            })
            silverImagesWrapper?.forEach((wrap) => {
                wrap.style.display = "none";
            })
            sliderThumbnailImage.style.display = "none";
            sliderPreviewImage.style.display = "none";
        }

        engravingTexts.forEach((textContainer) => {
            if(color === "gold"){
                textContainer.classList.remove("silver");
            }
            if(color === "silver" || color === "white gold"){
                textContainer.classList.add("silver");
            }
        })
        engravedTextMobile.forEach((textContainer) => {
            if(color === "gold"){
                textContainer.classList.remove("silver");
            }
            if(color === "silver" || color === "white gold"){
                textContainer.classList.add("silver");
            }
        });
    }

    // first render call
    showCorrectVarinatCustomizeImage("");

    const resetEverything = () => {
        savedEngravedText.value = "",
        eternalizeCheckbox.checked = false;
        engravingTexts.forEach((textContainer) => {
            textContainer.textContent = "";
        });
        engravedTextMobile.forEach((textContainer) => {
            textContainer.textContent = "";
        });
        engravingMobilePreview.classList.remove("show");
        showCorrectVarinatCustomizeImage("");
        makeityourSelectButton.classList.remove("active");
    }

    document.addEventListener('on:variant:change', (event) => {
        const color = event?.detail?.variant?.title?.toLowerCase();
        const oldColor = selectedColor;
        selectedColor = defaultColor?.textContent?.toLowerCase() || "";
        // if(oldColor !== selectedColor){
        //     resetEverything();
        // }
        resetEverything();
    });

    // click on radio button start of make it your process
    makeityourSelectButton.addEventListener("click", () => {
        makeityourSelectButton.classList.toggle("active");
        const mainGoldORSilverImage = document.querySelector("[data-engrave]");
        const engravingTextInImage = mainGoldORSilverImage.querySelector(".engraving__text");
        if (makeityourSelectButton.classList.contains("active")) {
            engravingMobilePreview.classList.add("show");
            engravingTextInImage.style.display = "block";
            showCorrectVarinatCustomizeImage(selectedColor);

            const thumbnail = sliderThumbnailImage.querySelector(".thumbnail--media-image");
            thumbnail.click();
        }else {
            resetEverything();
        }
    })

    // on hover of section doing opacity 1
    makeityourContainer.addEventListener("mouseenter", () => {
        engravingTexts.forEach((textContainer) => {
            textContainer.style.opacity = 1;
        })
    });

    makeityourContainer.addEventListener("mouseleave", () => {
        engravingTexts.forEach((textContainer) => {
            // when the eternalize if checked then we always show the text else do opacity 0 on mouse move from the section
            if(eternalizeCheckbox.checked === true){
                 textContainer.style.opacity = 1;
            }else {
                textContainer.style.opacity = 0;
            }
        })
    });

    // handle the input box
    savedEngravedText.addEventListener("input", (e) => {
        engravingTexts.forEach((textContainer) => {
            textContainer.textContent = e.target.value;
        })
        engravedTextMobile.forEach((textContainer) => {
            textContainer.textContent = e.target.value;
        });
        const thumbnail = sliderThumbnailImage.querySelector(".thumbnail--media-image");
        thumbnail.click();
    });

    // hide the engrave text from thumbnail images
    thumbnailMediaImage.forEach((thumbnail) => {
        const engravingText = thumbnail.querySelector(".engraving__text");
        engravingText.style.display = "none";
    })
})